import type { FilterOp, ReportFilter, FieldReference } from "../api/types";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import DateObject from "react-date-object";

const OP_LABELS: Record<FilterOp, string> = {
  eq: "برابر با",
  gt: "بزرگ‌تر از",
  lt: "کوچک‌تر از",
  gte: "بزرگ‌تر یا مساوی",
  lte: "کوچک‌تر یا مساوی",
  in: "یکی از",
  date_range: "بازهٔ تاریخ",
};

interface Props {
  availableFields: FieldReference[];
  filter: ReportFilter;
  onChange: (next: ReportFilter) => void;
  onRemove: () => void;
}

export function FilterRowEditor({ availableFields, filter, onChange, onRemove }: Props) {
  const getFieldKey = (f: { path?: string[]; field: string }) => (f.path ? f.path.join('.') + '.' : '') + f.field;
  const currentKey = getFieldKey(filter);

  const handleFieldChange = (key: string) => {
    const selected = availableFields.find((f) => getFieldKey(f) === key);
    if (selected) {
      onChange({ ...filter, path: selected.path, field: selected.field });
    }
  };

  const isDate = filter.op === 'date_range' || currentKey.includes("date") || currentKey.endsWith("_at") || currentKey.includes("time");

  const handleDateChange = (dateObj: any) => {
    if (filter.op === 'date_range') {
      const dates = dateObj as DateObject[] | null;
      if (Array.isArray(dates) && dates.length === 2) {
        const isoDates = dates.map(d => new DateObject(d).convert(gregorian, gregorian_en).format("YYYY-MM-DD"));
        onChange({ ...filter, value: isoDates });
      } else {
        onChange({ ...filter, value: "" });
      }
    } else {
      const date = dateObj as DateObject | null;
      if (date) {
        const isoDate = new DateObject(date).convert(gregorian, gregorian_en).format("YYYY-MM-DD");
        onChange({ ...filter, value: isoDate });
      } else {
        onChange({ ...filter, value: "" });
      }
    }
  };

  const getDateValue = () => {
    if (filter.op === 'date_range') {
      return Array.isArray(filter.value) && filter.value.length === 2
        ? filter.value.map(d => new DateObject({ date: d as string, format: "YYYY-MM-DD", calendar: gregorian, locale: gregorian_en }))
        : [];
    }
    return typeof filter.value === 'string' && filter.value
      ? new DateObject({ date: filter.value, format: "YYYY-MM-DD", calendar: gregorian, locale: gregorian_en })
      : "";
  };

  return (
    <div className="flex items-center gap-2 rounded-md border p-2 bg-white">
      <select
        className="rounded border px-2 py-1 text-sm bg-gray-50"
        value={currentKey}
        onChange={(e) => handleFieldChange(e.target.value)}
      >
        {availableFields.map((f) => {
          const key = getFieldKey(f);
          const readableKey = key.replace(/__/g, " - ").replace(/_/g, " ");
          return (
            <option key={key} value={key} dir="ltr">
              {f.alias ? `${f.alias} (${readableKey})` : readableKey}
            </option>
          );
        })}
      </select>

      <select
        className="rounded border px-2 py-1 text-sm bg-gray-50"
        value={filter.op}
        onChange={(e) => onChange({ ...filter, op: e.target.value as FilterOp, value: e.target.value === 'date_range' ? [] : "" })}
      >
        {(Object.keys(OP_LABELS) as FilterOp[]).map((op) => (
          <option key={op} value={op}>
            {OP_LABELS[op]}
          </option>
        ))}
      </select>

      {isDate ? (
        <DatePicker
          range={filter.op === 'date_range'}
          calendar={persian}
          locale={persian_fa}
          calendarPosition="bottom-right"
          inputClass="flex-1 rounded border px-2 py-1 text-sm min-w-[200px]"
          value={getDateValue()}
          onChange={handleDateChange}
        />
      ) : (
        <input
          className="flex-1 rounded border px-2 py-1 text-sm"
          value={String(filter.value ?? "")}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          placeholder="مقدار"
        />
      )}

      <button
        onClick={onRemove}
        className="text-sm text-red-600 hover:underline shrink-0"
        type="button"
      >
        حذف
      </button>
    </div>
  );
}
